
<template>
  <div v-click-outside="closeRubricList">
    <div
      class="settings__toggle"
      :class="{ 
        'settings__toggle--active': showRubricList,
        'settings__toggle--clicable': filtredCategoriesLength()
      }"
      @click="toggleRubric"
    >
      <template v-if="props.selectedChart === 0 && rubricsStore?.allThemes">
        {{ t('news_page_themes_edit_popup.label.theme') }}
      </template>
      <template v-else-if="props.selectedChart === 1 && rubricsStore?.allRubrics">
        {{ t('news_page_themes_edit_popup.label.rubric') }}
      </template>
      <template v-else-if="props.selectedChart === 2 && newsStorage?.filterCategories?.list">
        {{ t('news_page_body_post.label.category') }}
      </template>
      ({{ filtredCategoriesLength() }})
      <ArrowBottomIcon
        v-if="categories.length"
        class="settings__toggle-svg"
        :class="{ 'settings__toggle-svg--active' : showRubricList }"
      />
    </div>
    <div
      v-if="showRubricList"
      class="settings-modal"
    >
      <ChartChooser
        v-if="props.selectedChart !== 2"
        v-model="selectedChart"
        name="RubricListChart"
      />

      <ListView class="scroll settings-modal__list">
        <ListItem
          v-for="item in categoriesToShow"
          :key="item.id"
          v-model="selectedRubric"
          :use-checkbox="true"
          :item="item.id"
          :disabled="item.is_disabled && !props.allEnabled"
        >
          {{ item.name }}
        </ListItem>
      </ListView>
      <ButtonAccentPrimary @click="updateRubric">
        {{ t('sentiment_stat_bar.apply') }}
      </ButtonAccentPrimary>
    </div>
  </div>
</template>

<script setup>
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { useBlockStore } from "@/store/NewsFeed/blocks";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { computed, defineProps, nextTick, onMounted, ref, toRaw, watch, defineEmits } from "vue";
import { t } from "@/controllerLK/GlobusLocalization";
import ChartChooser from "./ChartChooser.vue";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";

const emit = defineEmits(["update:selectedChart", "clearRubrics"]);

const props = defineProps({
  selectedChart: {
    type: Number,
    required: true
  },
  activeStore: {
    type: Object,
    required: true
  },
  date: {
    type: Array,
    required: false
  },
  active: {
    type: Boolean,
    default: true
  },
  allEnabled: {
    type: Boolean,
    default: false,
  },
  sendParamName: {
    type: Boolean,
    default: false
  },
  stopLoadOnClear: {
    type: Boolean,
    default: false
  }
});

const rubricsStore = useRubricsStore();
const blockStore = useBlockStore();
const newsStorage = useNewsFeedStore();
const selectedChart = ref(props.selectedChart);

watch(
  () => props.selectedChart,
  (newVal) => {
    selectedChart.value = newVal;
  }
);

watch(
  () => selectedChart.value,
  (newVal) => {
    emit("update:selectedChart", newVal);
  }
);

const showRubricList = ref(false);
const selectedRubric = ref([]);

watch(
  () => props.selectedChart,
  () => {
    applyFiltredCategories();
    fillSelectedRubrics();
  },
);

watch(
  () => newsStorage.filterCategories.list.length,
  async () => {
    await refreshFiltredCategories();
  },
  { deep: false }
);

const categories = computed(() => {
  switch (selectedChart.value) {
    case 1:
      return rubricsStore.allRubrics.filter((rubric) => rubric?.is_fixed);
    case 2:
      return newsStorage.filterCategories.list;
    default:
      return blockStore.allThemes;
  }
});

const activeCategories = ref([]);

const filtredCategories = ref([]);

const filtredCategoriesLength = () => {
  if (!props.active) return 0;
  if (selectedRubric?.value?.length) return selectedRubric.value.length;
  if (filtredCategories?.value?.length) return [...filtredCategories.value].filter((item) => !item.is_disabled).length;
  return categories.value.length;
};

const closeRubricList  = () => {
  showRubricList.value = false;
};

const applyFiltredCategories  = () => {
  const kind = selectedChart.value === 0 ? blockStore.allThemes : rubricsStore.allRubrics;
  activeCategories.value = props.activeStore?.items?.map((item) => {
    if (!isNaN(item.name)) {
      return kind.find((theme) => theme.id == item.name)?.name;
    }
    return item.name;
  }) || [];
  filtredCategories.value = [...categories.value].map((item) => {
    item.is_disabled = !activeCategories.value.includes(item.name);
    return item;
  });
};

const fillSelectedRubrics = async () => {
  // Получаем обычный массив без реактивности
  const rawCategories = toRaw(filtredCategories.value);

  // Формируем список ID активных категорий
  const activeIDs = rawCategories
    .filter((item) => !item.is_disabled || props.allEnabled)
    .map((item) => item.id);

  // Проверяем, изменилось ли значение, чтобы избежать лишнего рендера
  if (JSON.stringify(selectedRubric.value) !== JSON.stringify(activeIDs)) {
    await nextTick(); // Подождём завершения текущего рендера
    selectedRubric.value = activeIDs;
  }
};

const categoriesToShow = computed(() => {
  return filtredCategories?.value?.length ? filtredCategories.value : categories.value;
});

const updateRubric = async () => {
  let paramName;
  switch (props.selectedChart) {
    case 1:
      paramName = "rubricIDs";
      break;
    case 2:
      paramName = "categoryIDs";
      break;
    default:
      paramName = "blockIDs";
      break;
  }
  // if (selectedRubric.value.length === filtredCategories.value.length) selectedRubric.value = [];
  if (selectedRubric.value.length === 0) {
    emit("clearRubrics");
    if (props.stopLoadOnClear) {
      showRubricList.value = false;
      return;
    }
  }
  if (props.date) {
    if (props.sendParamName) {
      await props.activeStore.loadStat(props.date[0], props.date[1], paramName, selectedRubric.value);
    } else {
      await props.activeStore.loadStat(props.date[0], props.date[1], selectedRubric.value);
    }
  } else {
    await props.activeStore.loadStat(selectedRubric.value);
  }
  showRubricList.value = false;
};

const refreshFiltredCategories = async () => {
  if (selectedChart.value === 2) {
    const ids = props.activeStore?.items.map((item) => Number(item.name));
    filtredCategories.value = [...categories.value].map((item) => {
      item.is_disabled = !ids.includes(item.id);
      return item;
    });
  } else {
    applyFiltredCategories();
  }
  await fillSelectedRubrics();
};
const toggleRubric = () => {
  if (props.active) showRubricList.value = !showRubricList.value;
};

onMounted(() => {
  nextTick(async () => {
    await refreshFiltredCategories();
  });
});
</script>

<style lang="scss" scoped>
.settings {
  &__toggle {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #F7F8FD;
    padding: 14px;
    border-radius: 8px;
    user-select: none;
    width: 150px; // По макету width: 130px. Не влазиет в одну строку, если выбрано > 9 тем.

    &-svg {
      color: var(--text-text-pur);
      transition: .2s;
      margin-left: auto;

      &--active {
        transform: rotate(180deg);
        color: #1F9AFF;
      }
    }

    &--active {
      color: #1F9AFF;
      border: #EEF1FF;
    }

    &--clicable {
      cursor: pointer;
    }
  }

  &-modal {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
    max-width: 330px;
    max-height: 379px;
    overflow-x: hidden;
    background: var(--background-bg-white);
    z-index: 40;
    position: absolute;
    top: 45px;
    right: 26px;
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 9px 28px 8px #0000001a;
    line-height: 18px;
    text-align: left;
    user-select: none;

    &__list {
      max-height: 290px;
    }
  }
}
</style>
